import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {$getRoot, LexicalEditor, ParagraphNode, SerializedEditorState, TextNode} from 'lexical';
import React, {forwardRef, useImperativeHandle, useRef,} from 'react';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary'
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ToolbarPlugin} from "./ToolbarPlugin";
import {HeadingNode} from "@lexical/rich-text";
import {ListItemNode, ListNode} from "@lexical/list";
import {ListPlugin} from "@lexical/react/LexicalListPlugin";
import throttle from 'lodash.throttle';

export interface RichTextEditorRef {
    getContent: () => SerializedEditorState | null;
    getTitle: () => string;
}

interface RichTextEditorProps {
    initialValue?: SerializedEditorState;
    onTitleChange?: (title: string) => void;
    onShouldAutoSave?: () => void;
}


export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
    ({initialValue, onTitleChange, onShouldAutoSave}, ref) => {
        const latestState = useRef<SerializedEditorState | null>(null);
        const latestTitle = useRef<string>('');

        const throttledShouldAutoSave = useRef(
            throttle(() => {
                onShouldAutoSave?.();
            }, 4000, { leading: false, trailing: true })
        ).current;


        const editorConfig = {
            namespace: 'JournalEditor',
            editable: true,
            editorState: (editor: LexicalEditor) => {
                if (initialValue) {
                    const parsed = editor.parseEditorState(initialValue);
                    editor.setEditorState(parsed);
                }
            },
            theme: {
                paragraph: 'mb-2',
                text: {
                    bold: 'font-bold',
                    italic: 'italic',
                },
            },
            onError(error: Error) {
                console.error('Lexical error:', error);
            },
            nodes: [
                ParagraphNode,
                TextNode,
                HeadingNode,
                ListNode,
                ListItemNode
            ]
        };

        useImperativeHandle(ref, () => ({
            getContent: () => latestState.current,
            getTitle: () => latestTitle.current
        }));

        return (
            <LexicalComposer initialConfig={editorConfig}>
                <ToolbarPlugin/>
                <RichTextPlugin
                    contentEditable={<ContentEditable className="
                    min-h-[150px] 
                    [&_h1]:text-2xl 
                    [&_h1]:font-bold
                    [&_h2]:text-xl [&_h2]:font-semibold
                    [&_h3]:text-lg
                    [&_ul]:list-disc 
                    [&_ol]:list-decimal 
                    [&_li]:ml-6
                    prose prose-sm text-gray-900 focus:outline-none 
                    "/>}
                    placeholder={<div className="absolute top-14 left-4 text-gray-400 pointer-events-none">
                        Write something...</div>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin/>
                <AutoFocusPlugin/>
                <ListPlugin/>
                <OnChangePlugin
                    onChange={(editorState) => {

                        editorState.read(() => {
                            const root = $getRoot();
                            const firstParagraphText = root.getChildren()
                                .find((node) => node.getType() === 'paragraph' && node.getTextContent().trim() !== '')
                                ?.getTextContent()
                                .trim();

                            const title = firstParagraphText?.slice(0, 99) ?? '';
                            latestTitle.current = title;
                            onTitleChange?.(title);
                        });

                        const json = editorState.toJSON();
                        latestState.current = json;

                        throttledShouldAutoSave();
                    }}
                />
            </LexicalComposer>
        );
    }
);

RichTextEditor.displayName = 'RichTextEditor';



